// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — Forms Types
// Form builder field types, schemas, templates, and responses
// ═══════════════════════════════════════════════════════════════

// Field types supported by the form builder
export type FormFieldType =
  | 'text' | 'number' | 'date' | 'email' | 'phone' | 'url'
  | 'textarea' | 'select' | 'multiselect' | 'checkbox' | 'radio'
  | 'photo' | 'gps' | 'signature' | 'file' | 'rating' | 'likert'
  | 'section' | 'instructions' | 'page_break';

export interface FormFieldOption {
  label: string;
  value: string;
}

export interface FormFieldConditional {
  fieldKey: string;      // which field to check
  operator: 'equals' | 'not_equals' | 'contains' | 'not_empty' | 'empty';
  value: string;
}

export interface FormField {
  key: string;           // unique field ID (uuid-like)
  type: FormFieldType;
  label: string;
  placeholder: string;
  helpText: string;
  required: boolean;
  options: FormFieldOption[];   // for select/radio/checkbox/likert
  validation: {
    minLength?: number;
    maxLength?: number;
    min?: number;
    max?: number;
    pattern?: string;
  };
  conditional?: FormFieldConditional;  // show/hide logic
  config: Record<string, unknown>;     // type-specific config (e.g., addressAutocomplete, likertScale)
}

export interface FormPage {
  title: string;
  fields: FormField[];
}

export interface FormSchema {
  title: string;
  description: string;
  pages: FormPage[];
  settings: {
    submitButtonText: string;
    successMessage: string;
    allowMultipleSubmissions: boolean;
    notifyOnSubmission: boolean;
    notifyEmail: string;
  };
}

export interface FormTemplate {
  id: number;
  Rid: number;
  templateName: string;
  description: string;
  schema: string;          // JSON-encoded FormSchema
  status: 'draft' | 'published' | 'archived';
  module: string;          // linked module ('' for standalone)
  incidentId: string;
  isPublic: string;        // 'true' / 'false'
  publicSlug: string;
  creatorName: string;
  responseCount: string;
  datecreated: string;
}

export interface FormResponse {
  id: number;
  Rid: number;
  templateId: string;
  templateName: string;
  incidentId: string;
  respondentName: string;
  respondentEmail: string;
  responseData: string;    // JSON-encoded Record<string, unknown>
  status: 'complete' | 'partial' | 'reviewed';
  submittedAt: string;
  sourceModule: string;
  notes: string;
  datecreated: string;
}

// Default empty field
export function makeField(type: FormFieldType = 'text'): FormField {
  return {
    key: 'f_' + Math.random().toString(36).slice(2, 9),
    type,
    label: '',
    placeholder: '',
    helpText: '',
    required: false,
    options: [],
    validation: {},
    config: {},
  };
}

// Default empty schema
export function makeSchema(): FormSchema {
  return {
    title: 'Untitled Form',
    description: '',
    pages: [{ title: 'Page 1', fields: [] }],
    settings: {
      submitButtonText: 'Submit',
      successMessage: 'Thank you! Your response has been recorded.',
      allowMultipleSubmissions: false,
      notifyOnSubmission: false,
      notifyEmail: '',
    },
  };
}

// Field type metadata for the toolbox
export const FIELD_TYPE_META: Record<FormFieldType, { label: string; icon: string; group: 'basic' | 'advanced' | 'layout' }> = {
  text:         { label: 'Text Input',      icon: 'Aa',  group: 'basic' },
  number:       { label: 'Number',          icon: '#',   group: 'basic' },
  date:         { label: 'Date',            icon: '📅',  group: 'basic' },
  email:        { label: 'Email',           icon: '@',   group: 'basic' },
  phone:        { label: 'Phone',           icon: '📞',  group: 'basic' },
  url:          { label: 'URL',             icon: '🔗',  group: 'basic' },
  textarea:     { label: 'Long Text',       icon: '¶',   group: 'basic' },
  select:       { label: 'Dropdown',        icon: '▼',   group: 'basic' },
  multiselect:  { label: 'Multi-Select',    icon: '☑',   group: 'basic' },
  checkbox:     { label: 'Checkboxes',      icon: '☑',   group: 'basic' },
  radio:        { label: 'Radio Buttons',   icon: '◉',   group: 'basic' },
  photo:        { label: 'Photo Capture',   icon: '📷',  group: 'advanced' },
  gps:          { label: 'GPS Location',    icon: '📍',  group: 'advanced' },
  signature:    { label: 'Signature',       icon: '✍',   group: 'advanced' },
  file:         { label: 'File Upload',     icon: '📎',  group: 'advanced' },
  rating:       { label: 'Rating Scale',    icon: '⭐',  group: 'advanced' },
  likert:       { label: 'Likert Scale',    icon: '📊',  group: 'advanced' },
  section:      { label: 'Section Header',  icon: '─',   group: 'layout' },
  instructions: { label: 'Instructions',    icon: 'ℹ',   group: 'layout' },
  page_break:   { label: 'Page Break',      icon: '⎯',   group: 'layout' },
};
